import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CreateAudioTrackPayload,
  CreatePlaylistPayload,
  MusicOnHoldApi,
  UpdateAudioTrackPayload,
  UpdatePlaylistPayload,
} from '../api/musicOnHoldApi.types'

const QUERY_KEYS = {
  playlists: (pbxId: number) => ['mohPlaylists', pbxId] as const,
  playlist: (playlistId: number) => ['mohPlaylist', playlistId] as const,
  audioTracks: (pbxId: number) => ['mohAudioTracks', pbxId] as const,
  audioTrack: (pbxId: number, uuid: string) =>
    ['mohAudioTrack', pbxId, uuid] as const,
}

export { QUERY_KEYS as MOH_QUERY_KEYS }

// ---------------------------------------------------------------------------
// Playlist queries
// ---------------------------------------------------------------------------

export const useGetPlaylists = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.playlists(pbxId ?? 0),
    queryFn: () => api.getPlaylists(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetPlaylist = (
  api: MusicOnHoldApi,
  playlistId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.playlist(playlistId ?? 0),
    queryFn: () => api.getPlaylist(playlistId!),
    enabled: !!playlistId,
    retry: false,
  })
}

// ---------------------------------------------------------------------------
// Playlist mutations
// ---------------------------------------------------------------------------

export const useCreatePlaylist = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreatePlaylistPayload) => api.createPlaylist(payload),
    onSuccess: () => {
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.playlists(pbxId),
        })
      }
    },
  })
}

export const useUpdatePlaylist = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number
      payload: UpdatePlaylistPayload
    }) => api.updatePlaylist(id, payload),
    onSuccess: (updatedPlaylist) => {
      queryClient.setQueryData(
        QUERY_KEYS.playlist(updatedPlaylist.id),
        updatedPlaylist,
      )
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.playlists(pbxId),
        })
      }
    },
  })
}

export const useDeletePlaylist = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deletePlaylist(id),
    onSettled: () => {
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.playlists(pbxId),
        })
      }
    },
  })
}

export const useSetDefaultPlaylist = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (playlistId: number) => {
      if (!pbxId) throw new Error('pbxId is required')
      return api.setDefaultPlaylist(pbxId, playlistId)
    },
    onSuccess: () => {
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.playlists(pbxId),
        })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Audio track queries
// ---------------------------------------------------------------------------

export const useGetAudioTracks = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.audioTracks(pbxId ?? 0),
    queryFn: () => api.getAudioTracks(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetAudioTrack = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
  uuid: string | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.audioTrack(pbxId ?? 0, uuid ?? ''),
    queryFn: () => api.getAudioTrack(pbxId!, uuid!),
    enabled: !!pbxId && !!uuid,
    retry: false,
    // The response carries a short-lived presigned file_url. Don't serve a
    // cached (possibly expired) URL on re-play: always refetch, and drop the
    // entry once the player unmounts so the next Play gets a fresh URL.
    staleTime: 0,
    gcTime: 0,
  })
}

// ---------------------------------------------------------------------------
// Audio track mutations
// ---------------------------------------------------------------------------

export const useCreateAudioTrack = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateAudioTrackPayload) =>
      api.createAudioTrack(payload),
    onSuccess: () => {
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.audioTracks(pbxId),
        })
      }
    },
  })
}

export const useUpdateAudioTrack = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string
      payload: UpdateAudioTrackPayload
    }) => {
      if (!pbxId) throw new Error('pbxId is required')
      return api.updateAudioTrack(pbxId, uuid, payload)
    },
    onSuccess: (updatedTrack) => {
      if (pbxId) {
        queryClient.setQueryData(
          QUERY_KEYS.audioTrack(pbxId, updatedTrack.uuid),
          updatedTrack,
        )
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.audioTracks(pbxId),
        })
      }
    },
  })
}

export const useDeleteAudioTrack = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => {
      if (!pbxId) throw new Error('pbxId is required')
      return api.deleteAudioTrack(pbxId, uuid)
    },
    onSettled: () => {
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.audioTracks(pbxId),
        })
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Playlist-track association mutations
// ---------------------------------------------------------------------------

export const useAddTrackToPlaylist = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      playlistId,
      audioTrackId,
    }: {
      playlistId: number
      audioTrackId: number
    }) => api.addTrackToPlaylist(playlistId, audioTrackId),
    onSuccess: (_data, { playlistId }) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.playlist(playlistId),
      })
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.playlists(pbxId),
        })
      }
    },
  })
}

export const useRemoveTrackFromPlaylist = (
  api: MusicOnHoldApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      playlistId,
      audioTrackId,
    }: {
      playlistId: number
      audioTrackId: number
    }) => api.removeTrackFromPlaylist(playlistId, audioTrackId),
    onSettled: (_data, _error, { playlistId }) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.playlist(playlistId),
      })
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.playlists(pbxId),
        })
      }
    },
  })
}
